/**
 * 树形数据工具函数
 * 用于将扁平化的数据转换为树形结构
 */

/**
 * 树节点接口
 * 包含 id 和可选的 parentId 字段
 */
export interface TreeNode {
  id: string | number;
  parentId?: string | number | null;
  [key: string]: unknown;
}

/**
 * 树形节点接口（包含 children）
 */
export type TreeStructure<T extends TreeNode> = T & {
  children?: TreeStructure<T>[];
  [key: string]: unknown;
};

/**
 * 将扁平化的数据转换为树形结构
 *
 * @param flatList - 扁平化的数据数组
 * @param options - 配置选项
 * @param options.parentField - 父节点字段名，默认为 'parentId'
 * @param options.childrenField - 子节点字段名，默认为 'children'
 * @param options.sortBy - 排序字段名，可选
 * @param options.sortOrder - 排序顺序，'asc' | 'desc'，默认为 'asc'
 * @returns 树形结构数组
 *
 * @example
 * ```typescript
 * const flatList = [
 *   { id: '1', name: 'A', parentId: null },
 *   { id: '2', name: 'B', parentId: '1' },
 *   { id: '3', name: 'C', parentId: '1' },
 * ];
 *
 * const tree = buildTree(flatList);
 * // 结果: [{ id: '1', name: 'A', children: [{ id: '2', name: 'B' }, { id: '3', name: 'C' }] }]
 * ```
 */
export function buildTree<T extends TreeNode>(
  flatList: T[],
  options: {
    parentField?: keyof T;
    childrenField?: string;
    sortBy?: keyof T;
    sortOrder?: 'asc' | 'desc';
  } = {}
): TreeStructure<T>[] {
  const {
    parentField = 'parentId' as keyof T,
    childrenField = 'children',
    sortBy,
    sortOrder = 'asc',
  } = options;

  // 创建节点映射表
  const nodeMap = new Map<string | number, TreeStructure<T>>();
  const roots: TreeStructure<T>[] = [];

  // 第一遍：创建所有节点
  for (const node of flatList) {
    const nodeWithChildren = {
      ...node,
      [childrenField]: [] as TreeStructure<T>[],
    } as TreeStructure<T>;
    nodeMap.set(node.id, nodeWithChildren);
  }

  // 第二遍：构建父子关系
  for (const node of flatList) {
    const parentId = node[parentField] as string | number | null | undefined;
    const nodeWithChildren = nodeMap.get(node.id)!;

    if (parentId != null && nodeMap.has(parentId)) {
      // 有父节点，添加到父节点的 children
      const parent = nodeMap.get(parentId)!;
      const parentRecord = parent as Record<string, unknown>;
      if (!parentRecord[childrenField]) {
        parentRecord[childrenField] = [] as TreeStructure<T>[];
      }
      (parentRecord[childrenField] as TreeStructure<T>[]).push(nodeWithChildren);
    } else {
      // 根节点
      roots.push(nodeWithChildren);
    }
  }

  // 排序函数
  const sortNodes = (nodes: TreeStructure<T>[]): TreeStructure<T>[] => {
    let sorted = [...nodes];

    if (sortBy) {
      sorted = sorted.sort((a, b) => {
        const aValue = (a as T)[sortBy];
        const bValue = (b as T)[sortBy];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const aStr = String(aValue);
        const bStr = String(bValue);
        return sortOrder === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      });
    }

    // 递归排序子节点
    return sorted.map((node) => ({
      ...node,
      [childrenField]: node[childrenField]
        ? sortNodes(node[childrenField] as TreeStructure<T>[])
        : undefined,
    }));
  };

  return sortNodes(roots);
}

/**
 * 将树形结构扁平化
 *
 * @param tree - 树形结构数组
 * @param options - 配置选项
 * @param options.childrenField - 子节点字段名，默认为 'children'
 * @param options.includeChildren - 是否在扁平化结果中保留 children 字段，默认为 false
 * @returns 扁平化的数据数组
 *
 * @example
 * ```typescript
 * const tree = [
 *   { id: '1', name: 'A', children: [{ id: '2', name: 'B' }] },
 * ];
 *
 * const flatList = flattenTree(tree);
 * // 结果: [{ id: '1', name: 'A' }, { id: '2', name: 'B' }]
 * ```
 */
export function flattenTree<T extends TreeNode>(
  tree: TreeStructure<T>[],
  options: {
    childrenField?: string;
    includeChildren?: boolean;
  } = {}
): T[] {
  const { childrenField = 'children', includeChildren = false } = options;
  const result: T[] = [];

  const traverse = (nodes: TreeStructure<T>[]): void => {
    for (const node of nodes) {
      const { [childrenField]: children, ...rest } = node;
      const flatNode = includeChildren ? (node as T) : (rest as T);
      result.push(flatNode);

      if (Array.isArray(children) && children.length > 0) {
        traverse(children as TreeStructure<T>[]);
      }
    }
  };

  traverse(tree);
  return result;
}

/**
 * 在树中查找节点
 *
 * @param tree - 树形结构数组
 * @param id - 要查找的节点 ID
 * @param options - 配置选项
 * @param options.childrenField - 子节点字段名，默认为 'children'
 * @returns 找到的节点，如果未找到返回 undefined
 */
export function findNodeInTree<T extends TreeNode>(
  tree: TreeStructure<T>[],
  id: string | number,
  options: { childrenField?: string } = {}
): TreeStructure<T> | undefined {
  const { childrenField = 'children' } = options;

  for (const node of tree) {
    if (node.id === id) {
      return node;
    }

    const children = node[childrenField] as TreeStructure<T>[] | undefined;
    if (children && children.length > 0) {
      const found = findNodeInTree(children, id, options);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
}

/**
 * 获取节点的所有祖先节点（从根到父节点）
 *
 * @param tree - 树形结构数组
 * @param id - 目标节点 ID
 * @param options - 配置选项
 * @param options.childrenField - 子节点字段名，默认为 'children'
 * @returns 祖先节点数组，从根节点到直接父节点
 */
export function getAncestors<T extends TreeNode>(
  tree: TreeStructure<T>[],
  id: string | number,
  options: { childrenField?: string } = {}
): TreeStructure<T>[] {
  const { childrenField = 'children' } = options;
  const ancestors: TreeStructure<T>[] = [];

  const findPath = (nodes: TreeStructure<T>[], path: TreeStructure<T>[]): boolean => {
    for (const node of nodes) {
      const currentPath = [...path, node];

      if (node.id === id) {
        ancestors.push(...path);
        return true;
      }

      const children = node[childrenField] as TreeStructure<T>[] | undefined;
      if (children && children.length > 0) {
        if (findPath(children, currentPath)) {
          return true;
        }
      }
    }

    return false;
  };

  findPath(tree, []);
  return ancestors;
}

